import { AccessoriesPageData } from 'src/modules/accessories/AccessoriesPageMapping.ts';
import { UserSettings } from 'src/types/UserSettings.ts';
import { getPriceValue } from 'src/modules/checkout/getPriceValue.ts';
import { sortAccessoriesBy } from 'src/modules/common/utils/sortAccessories.ts';
import { ComboData } from 'src/modules/checkout/CheckoutPageMapping.ts';
import { ComboState, LocationState } from 'src/modules/LocationState.ts';
import { parseRecommended } from 'src/modules/common/utils/parseRecommended.ts';

export type AccessoryData = {
    accessory: AccessoriesPageData['vssAccessories']['valveAccessories']['0'];
    price: number;
    qty: number;
};

export const calculateTotals = (
    combo: ComboData,
    {
        valve,
        actuator,
        actuatorAccessories,
        valveAccessories,
    }: AccessoriesPageData['vssAccessories'],
    userSettings: UserSettings,
    checkoutState: Omit<LocationState, 'combo'> & { combo: ComboState },
    currency: string | null,
): {
    subtotal: number;
    accessoriesSubtotal: number;
    total: number;
    currentCurrency: string;
    accessories: AccessoryData[];
    pricePerUnit: number;
} => {
    const comboPrice = getPriceValue(combo, userSettings.shoppingCenter as keyof typeof combo, currency);
    const valvePrice = getPriceValue(valve, userSettings.shoppingCenter as keyof typeof valve, currency);
    const actuatorPrice = actuator
        ? getPriceValue(actuator, userSettings.shoppingCenter as keyof typeof actuator, currency)
        : { currency: '', value: 0 };

    const pricePerUnit = comboPrice && comboPrice.value > 0
        ? comboPrice.value
        : (valvePrice ? valvePrice.value : 0) + (actuatorPrice ? actuatorPrice.value : 0);

    const currentCurrency = comboPrice?.currency || valvePrice?.currency || currency || 'USD';
    const subtotal = pricePerUnit * checkoutState.combo.qty;

    const { isRecommended } = parseRecommended(valve.connectors);

    const accessories = [
        ...actuatorAccessories || [],
        ...valveAccessories,
    ].reduce((acc, curr) => {
        const exist = acc.find((item) => item.accessory.item.Label === curr.item.Label);
        if (!exist && checkoutState.selectedAccessories.find(({ label }) => curr.item.Label === label) !== undefined) {
            const price = getPriceValue(
                curr.item,
                userSettings.shoppingCenter as keyof typeof curr.item,
                currency,
            );
            const accessoryItem = checkoutState.selectedAccessories.find(({ label }) => label === curr.item.Label);
            const qty = accessoryItem ? accessoryItem.qty : 1;
            acc.push({
                accessory: curr,
                price: price?.value || 0,
                qty,
            });
        }
        return acc;
    }, [] as AccessoryData[])
        .sort((a, b) => sortAccessoriesBy(isRecommended)(a.accessory, b.accessory));

    const accessoriesSubtotal = accessories
        .reduce((acc, { price, qty }) => acc + (price * qty), 0);

    const total = subtotal + accessoriesSubtotal;

    return {
        subtotal,
        accessoriesSubtotal,
        total,
        currentCurrency,
        accessories,
        pricePerUnit,
    };
};
